import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, DatePicker, Input, Pagination, Select, Spin } from "antd";
import dayjs from "dayjs";
import moment from "moment-timezone";
import {
  AlertTriangle,
  BellRing,
  CalendarDays,
  CheckCircle2,
  Clock3,
  RefreshCw,
  Search,
  ShieldAlert,
  XCircle,
} from "lucide-react";
import { getNotificationDeliveryBoard } from "../../../../Service/RM";

const IST_TIMEZONE = "Asia/Kolkata";

const REMINDER_LABELS = {
  "10HoursBeforeStartDuty": "10 hours before",
  "3HoursBeforeStartDuty": "3 hours before",
  "10MinutesBeforeStartDuty": "10 minutes before",
  "1HourBeforeEndDuty": "1 hour before end",
};

const STATUS_STYLES = {
  scheduled: {
    label: "Scheduled",
    className: "border-blue-200 bg-blue-50 text-blue-700",
    icon: Clock3,
  },
  processing: {
    label: "Processing",
    className: "border-violet-200 bg-violet-50 text-violet-700",
    icon: RefreshCw,
  },
  executed: {
    label: "Executed",
    className: "border-emerald-200 bg-emerald-50 text-emerald-700",
    icon: CheckCircle2,
  },
  failed: {
    label: "Failed",
    className: "border-red-200 bg-red-50 text-red-700",
    icon: XCircle,
  },
};

const VALIDATION_STYLES = {
  correct: {
    label: "Correct",
    className: "border-emerald-200 bg-emerald-50 text-emerald-700",
    icon: CheckCircle2,
  },
  stale_schedule: {
    label: "Stale schedule",
    className: "border-amber-200 bg-amber-50 text-amber-700",
    icon: AlertTriangle,
  },
  wrong_hcp: {
    label: "Wrong HCP",
    className: "border-red-200 bg-red-50 text-red-700",
    icon: XCircle,
  },
  orphan: {
    label: "Orphan",
    className: "border-red-200 bg-red-50 text-red-700",
    icon: ShieldAlert,
  },
  duplicate: {
    label: "Duplicate",
    className: "border-orange-200 bg-orange-50 text-orange-700",
    icon: AlertTriangle,
  },
  legacy: {
    label: "Legacy",
    className: "border-slate-200 bg-slate-50 text-slate-600",
    icon: AlertTriangle,
  },
};

const EMPTY_SUMMARY = {
  upcoming: 0,
  dueToday: 0,
  executed: 0,
  attentionNeeded: 0,
};

const SummaryItem = ({ icon: Icon, label, value, tone, withDivider }) => {
  const tones = {
    blue: "border-blue-200 bg-blue-50 text-blue-600",
    amber: "border-amber-200 bg-amber-50 text-amber-600",
    green: "border-emerald-200 bg-emerald-50 text-emerald-600",
    red: "border-red-200 bg-red-50 text-red-600",
  };

  return (
    <div
      className={`flex min-h-[92px] items-center gap-4 px-5 py-4 sm:px-6 ${
        withDivider ? "border-t border-slate-200 sm:border-l sm:border-t-0" : ""
      }`}
    >
      <div
        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border ${tones[tone]}`}
      >
        <Icon size={22} strokeWidth={1.9} />
      </div>
      <div>
        <p className="text-xs font-medium text-slate-600">{label}</p>
        <p className={`mt-1 text-2xl font-semibold leading-none ${tones[tone].split(" ").at(-1)}`}>
          {Number(value || 0).toLocaleString("en-IN")}
        </p>
      </div>
    </div>
  );
};

const StateBadge = ({ value, styles }) => {
  const config = styles[value] || styles.legacy || STATUS_STYLES.scheduled;
  const Icon = config.icon;

  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border px-2.5 py-1 text-[11px] font-medium ${config.className}`}
    >
      <Icon size={13} strokeWidth={2} />
      {config.label}
    </span>
  );
};

const formatDateTimeIST = (value) => {
  if (!value) return "Not available";
  const parsed = moment(value);
  if (!parsed.isValid()) return "Not available";
  return parsed.tz(IST_TIMEZONE).format("DD MMM YYYY, hh:mm A");
};

const formatDutyWindowIST = (job) => {
  const start = moment(job?.dutyStartTime);
  const end = moment(job?.dutyEndTime);
  if (!start.isValid()) return "Duty not found";

  const dateLabel = start.tz(IST_TIMEZONE).format("DD MMM YYYY");
  const startLabel = start.tz(IST_TIMEZONE).format("hh:mm A");
  const endLabel = end.isValid()
    ? end.tz(IST_TIMEZONE).format("hh:mm A")
    : "Not available";
  return `${dateLabel}, ${startLabel} - ${endLabel}`;
};

const DutyReminderMonitor = () => {
  const [jobs, setJobs] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [bookingDraft, setBookingDraft] = useState("");
  const [filters, setFilters] = useState({
    bookingID: "",
    dutyDate: "",
    reminderType: "",
    status: "",
    validation: "",
  });
  const requestIDRef = useRef(0);

  useEffect(() => {
    const debounce = setTimeout(() => {
      const nextBookingID = bookingDraft.trim();
      setPage(1);
      setFilters((current) =>
        current.bookingID === nextBookingID
          ? current
          : { ...current, bookingID: nextBookingID },
      );
    }, 350);

    return () => clearTimeout(debounce);
  }, [bookingDraft]);

  const fetchBoard = useCallback(
    async ({ silent = false } = {}) => {
      const requestID = requestIDRef.current + 1;
      requestIDRef.current = requestID;
      if (!silent) setLoading(true);
      setError("");

      const result = await getNotificationDeliveryBoard({
        ...filters,
        page,
        pageSize,
      });

      if (requestID !== requestIDRef.current) return;

      if (result?.error === false) {
        setJobs(Array.isArray(result?.data?.jobs) ? result.data.jobs : []);
        setSummary(result?.data?.summary || EMPTY_SUMMARY);
        setTotal(Number(result?.data?.pagination?.total || 0));
        setLastUpdated(result?.data?.lastUpdated || new Date().toISOString());
      } else {
        setJobs([]);
        setSummary(EMPTY_SUMMARY);
        setTotal(0);
        setError(result?.message || "Unable to load scheduled reminders");
      }

      setLoading(false);
    },
    [filters, page, pageSize],
  );

  useEffect(() => {
    fetchBoard();
  }, [fetchBoard]);

  useEffect(() => {
    const refreshTimer = setInterval(() => {
      fetchBoard({ silent: true });
    }, 60000);

    return () => clearInterval(refreshTimer);
  }, [fetchBoard]);

  const updateFilter = (key, value) => {
    setPage(1);
    const nextValue = value || "";
    setFilters((current) =>
      current[key] === nextValue ? current : { ...current, [key]: nextValue },
    );
  };

  const startResult = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endResult = Math.min(page * pageSize, total);

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryItem
          icon={CalendarDays}
          label="Upcoming"
          value={summary.upcoming}
          tone="blue"
        />
        <SummaryItem
          icon={Clock3}
          label="Due today"
          value={summary.dueToday}
          tone="amber"
          withDivider
        />
        <SummaryItem
          icon={CheckCircle2}
          label="Executed"
          value={summary.executed}
          tone="green"
          withDivider
        />
        <SummaryItem
          icon={AlertTriangle}
          label="Attention needed"
          value={summary.attentionNeeded}
          tone="red"
          withDivider
        />
      </div>

      <div className="border-t border-slate-200 px-4 py-4 sm:px-5">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-[1.15fr_1fr_1fr_0.85fr_0.9fr_auto] xl:items-end">
          <div>
            <label className="mb-1.5 block text-[11px] font-medium text-slate-600">
              Booking ID
            </label>
            <Input
              value={bookingDraft}
              onChange={(event) => setBookingDraft(event.target.value)}
              placeholder="Search booking ID"
              prefix={<Search size={15} className="text-slate-400" />}
              allowClear
            />
          </div>

          <div>
            <label className="mb-1.5 block text-[11px] font-medium text-slate-600">
              Duty date
            </label>
            <DatePicker
              value={filters.dutyDate ? dayjs(filters.dutyDate) : null}
              onChange={(_, dateString) => updateFilter("dutyDate", dateString)}
              format="YYYY-MM-DD"
              placeholder="Select date"
              className="w-full"
              allowClear
            />
          </div>

          <div>
            <label className="mb-1.5 block text-[11px] font-medium text-slate-600">
              Reminder type
            </label>
            <Select
              value={filters.reminderType || undefined}
              onChange={(value) => updateFilter("reminderType", value)}
              placeholder="All"
              className="w-full"
              allowClear
              options={Object.entries(REMINDER_LABELS).map(([value, label]) => ({
                value,
                label,
              }))}
            />
          </div>

          <div>
            <label className="mb-1.5 block text-[11px] font-medium text-slate-600">
              Status
            </label>
            <Select
              value={filters.status || undefined}
              onChange={(value) => updateFilter("status", value)}
              placeholder="All"
              className="w-full"
              allowClear
              options={Object.entries(STATUS_STYLES).map(([value, config]) => ({
                value,
                label: config.label,
              }))}
            />
          </div>

          <div>
            <label className="mb-1.5 block text-[11px] font-medium text-slate-600">
              Validation
            </label>
            <Select
              value={filters.validation || undefined}
              onChange={(value) => updateFilter("validation", value)}
              placeholder="All"
              className="w-full"
              allowClear
              options={Object.entries(VALIDATION_STYLES).map(
                ([value, config]) => ({ value, label: config.label }),
              )}
            />
          </div>

          <div className="flex flex-col items-stretch xl:items-end">
            <Button
              icon={<RefreshCw size={15} />}
              onClick={() => fetchBoard()}
              className="border-blue-500 text-blue-600"
            >
              Refresh
            </Button>
            <span className="mt-1.5 whitespace-nowrap text-[10px] text-slate-400">
              {lastUpdated
                ? `Last updated ${moment(lastUpdated).fromNow()}`
                : "Waiting for latest data"}
            </span>
          </div>
        </div>
      </div>

      {error ? (
        <div className="mx-4 mb-4 flex items-center justify-between gap-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 sm:mx-5">
          <span>{error}</span>
          <Button size="small" onClick={() => fetchBoard()}>
            Retry
          </Button>
        </div>
      ) : null}

      <div className="overflow-x-auto border-t border-slate-200">
        <table className="w-full min-w-[1080px] border-collapse text-left">
          <thead className="bg-slate-50/80">
            <tr className="border-b border-slate-200 text-[11px] font-semibold text-slate-600">
              <th className="px-5 py-3">Booking ID</th>
              <th className="px-4 py-3">Duty date & time</th>
              <th className="px-4 py-3">Specialist</th>
              <th className="px-4 py-3">Reminder</th>
              <th className="px-4 py-3">Scheduled time</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-5 py-3">Validation</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan={7} className="h-56 text-center">
                  <Spin size="large" />
                  <p className="mt-3 text-xs text-slate-500">
                    Loading scheduled reminders
                  </p>
                </td>
              </tr>
            ) : jobs.length === 0 ? (
              <tr>
                <td colSpan={7} className="h-56 text-center">
                  <BellRing
                    size={30}
                    strokeWidth={1.5}
                    className="mx-auto text-slate-300"
                  />
                  <p className="mt-3 text-sm font-medium text-slate-600">
                    No scheduled reminders found
                  </p>
                  <p className="mt-1 text-xs text-slate-400">
                    Try clearing one or more filters.
                  </p>
                </td>
              </tr>
            ) : (
              jobs.map((job) => (
                <tr
                  key={job.id}
                  className="text-xs text-slate-700 transition-colors hover:bg-blue-50/30"
                >
                  <td className="whitespace-nowrap px-5 py-3.5 font-medium text-[#09244A]">
                    {job.bookingID || "Unavailable"}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3.5">
                    {formatDutyWindowIST(job)}
                  </td>
                  <td className="max-w-[180px] truncate px-4 py-3.5">
                    {job.specialistName || "Specialist"}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3.5">
                    {REMINDER_LABELS[job.reminderType] || "Legacy reminder"}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3.5">
                    {formatDateTimeIST(job.scheduledFor)}
                  </td>
                  <td className="px-4 py-3.5">
                    <StateBadge value={job.status} styles={STATUS_STYLES} />
                  </td>
                  <td className="px-5 py-3.5">
                    <StateBadge
                      value={job.validation}
                      styles={VALIDATION_STYLES}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col gap-3 border-t border-slate-200 px-5 py-4 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between">
        <span>
          Showing {startResult} to {endResult} of {total.toLocaleString("en-IN")} results
        </span>
        <Pagination
          current={page}
          pageSize={pageSize}
          total={total}
          onChange={(nextPage, nextPageSize) => {
            setPage(nextPageSize !== pageSize ? 1 : nextPage);
            setPageSize(nextPageSize);
          }}
          showSizeChanger
          pageSizeOptions={[10, 25, 50]}
          size="small"
          disabled={loading}
        />
      </div>
    </div>
  );
};

export default DutyReminderMonitor;
